// src/components/messaging/EmailMessageBody.tsx
//
// Display-only email body for the Unibox thread view. Shows ONLY the latest
// reply by default and hides the quoted previous thread (Gmail/Outlook
// chains), for BOTH directions (CRM-sent + externally-received).
//
// SELF-CONTAINED by design: the quote-stripping runs inline here (no shared
// module import) so the component can never render against a stale cached
// helper. Display-only — the stored message is never modified; the full body
// is re-shown via the "Show quoted text" toggle.

import React, { useState } from 'react';

interface EmailMessageBodyProps {
  bodyText: string | null;
  bodyHtml: string | null;
  className?: string;
}

/** HTML → plaintext (tags never injected). Block/<br> → newlines; entities decoded. */
function htmlToPlain(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(div|p|blockquote|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

/** A line that begins the quoted history. */
function isQuoteBoundary(line: string, next: string): boolean {
  const t = line.trim();
  if (/^>/.test(t)) return true; // quoted line ">"
  if (/^-{2,}\s*Original Message\s*-{2,}/i.test(t)) return true; // Outlook
  if (/^_{5,}$/.test(t)) return true; // Outlook divider
  if (/^From:\s*\S/i.test(t) && /^(Sent|Date|To|Subject|Cc):/i.test(next.trim())) return true;
  if (/^On\b.+\bwrote:?\s*$/.test(t)) return true; // Gmail "On … wrote:"
  // Gmail attribution wrapped across two lines.
  if (/^On\b/.test(t) && !/wrote:?\s*$/.test(t)) {
    if (/^On\b.+\bwrote:?\s*$/.test(`${t} ${next.trim()}`)) return true;
  }
  return false;
}

/**
 * Remove leading quote markers (`>`, `>>`, `>>>` …) from every line so the
 * expanded "Show quoted text" view reads as a clean conversation instead of
 * a wall of `>` symbols. Display-only — the stored body is untouched.
 */
function dequote(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((l) => l.replace(/^(\s*>)+\s?/, ''))
    .join('\n');
}

function computeVisible(
  bodyText: string | null,
  bodyHtml: string | null,
): { visible: string; hasQuoted: boolean; full: string } {
  const full =
    bodyText && bodyText.length > 0 ? bodyText : bodyHtml ? htmlToPlain(bodyHtml) : '';
  const lines = full.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (isQuoteBoundary(lines[i], lines[i + 1] || '')) {
      cut = i;
      break;
    }
  }

  let kept = cut === -1 ? lines.slice() : lines.slice(0, cut);
  // Belt-and-suspenders: never let a quote-marker line survive in the
  // default view, even if boundary detection missed it.
  kept = kept.filter((l) => !/^\s*>/.test(l));

  return { visible: kept.join('\n').trim(), hasQuoted: cut !== -1, full };
}

export const EmailMessageBody: React.FC<EmailMessageBodyProps> = ({
  bodyText,
  bodyHtml,
  className,
}) => {
  const [showQuoted, setShowQuoted] = useState(false);

  const { visible, hasQuoted, full } = computeVisible(bodyText, bodyHtml);
  const collapsedToEmpty = hasQuoted && !showQuoted && visible.length === 0;

  return (
    <>
      {collapsedToEmpty ? (
        <p className={className} data-testid="email-quoted-hidden">
          <span className="italic opacity-70">Quoted history hidden</span>
        </p>
      ) : (
        <p className={className} data-testid="email-body-text">
          {showQuoted ? dequote(full) : visible}
        </p>
      )}
      {hasQuoted && (
        <button
          type="button"
          data-testid="email-quote-toggle"
          onClick={() => setShowQuoted((v) => !v)}
          className="mt-1 text-xs underline opacity-70 hover:opacity-100"
        >
          {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
        </button>
      )}
    </>
  );
};

export default EmailMessageBody;
